import React, {
  useCallback, useContext, useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonBar, ErrorMessage, Input,
  LifeSaverForm,
  ScreenHeader, Select, TextArea
} from '../../components';
import { useCreateOrUpdate, useFormData } from '../../hooks';

import { NewFAContext } from '.';

const formConfig = {
  fields: [
    { id: 'cremation-facility', fieldName: 'cremation_facility', type: 'text' as const, required: true },
    { id: 'cremation-details', fieldName: 'cremation_details', type: 'text' as const, required: false },
    { id: 'embalming', fieldName: 'embalming', type: 'text' as const, required: false },
    { id: 'ashes-disposition', fieldName: 'ashes_disposition', type: 'text' as const, required: false },
    { id: 'ash-instructions', fieldName: 'ash_instructions', type: 'text' as const, required: false },
    { id: 'urn-type', fieldName: 'urn_type', type: 'text' as const, required: false },
    { id: 'urn-details', fieldName: 'urn_details', type: 'text' as const, required: false }
  ]
};


const embalmingOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not-sure', label: 'Not Sure' }
];

const ashDispositionOptions = [
  { value: 'scatter', label: 'Scatter Ashes' },
  { value: 'keep', label: 'Keep Ashes' },
  { value: 'burial', label: 'Burial of Ashes' },
  { value: 'other', label: 'Other' }
];

export function NewFACremationArrangements() {
  const navigate = useNavigate();
  const { funeralArrangement, update } = useContext(NewFAContext);
  const { handleCreateOrUpdate } = useCreateOrUpdate('funeral_arrangements');
  const { handleData, errors } = useFormData(formConfig);
    
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [ navigate ]);

  const handleNext = useCallback(async () => {
    const formData = handleData();
    
    // Validate required fields
    if (!formData.cremation_facility) {
      return;
    }

    const payload = {
      ...formData,
      user_id: funeralArrangement?.user_id || null,
      person_id: funeralArrangement?.person_id || null
    };

    try {
      await handleCreateOrUpdate(funeralArrangement?.id || '', payload);
      await update();
      navigate('../ceremony-preferences');
    } catch (err) {
      console.error('Error saving cremation arrangements:', err);
    }
  }, [ navigate, funeralArrangement, handleCreateOrUpdate, update, handleData ]);

  const memoizedEmbalmingOptions = useMemo(() => embalmingOptions, []);
  const memoizedAshDispositionOptions = useMemo(() => ashDispositionOptions, []);

  return (
    <div className="new-fa-disposition screen">
      <ScreenHeader 
        title="Cremation Arrangements"
        description="Specify your cremation preferences and arrangements."
        steps={6}
        step={1}
        handleBack={handleBack}
      />

      <LifeSaverForm 
        icon="location_on"
        header="Facility & Pre Cremation"
      >
        <Input
          id="cremation-facility"
          label="Cremation Facility"
        />
        <TextArea
          id="cremation-details"
          label="Cremation Details"
          style={{ maxWidth: '100%' }}
        />
        <Select
          id="embalming"
          name="embalming"
          label="Embalming"
          options={memoizedEmbalmingOptions}
          handleChange={() => {}}
        />
      </LifeSaverForm>
      <LifeSaverForm 
        icon="event_note"
        header="What to do with my ashes"
      >
        <Select
          id="ashes-disposition"
          name="ashes_disposition"
          label="Ashes Disposition"
          options={memoizedAshDispositionOptions}
          handleChange={() => {}}
        />
        <TextArea
          id="ash-instructions"
          name="ash_instructions"
          label="Ash Disposition Instructions"
          style={{ maxWidth: '100%' }}
        />
      </LifeSaverForm>
      <LifeSaverForm
        icon="deceased"
        header="Urn & Memorial"
      >
        <TextArea
          id="urn-type"
          name="urn_type"
          label="Urn Preference"
          placeholder="e.g. Wooden, Metal, Biodegradable or other details you prefer"
        />
        <TextArea
          id="urn-details"
          name="urn_details"
          label="Epitaph"
          style={{ maxWidth: '100%' }}
        />
      </LifeSaverForm>

      <ErrorMessage message={errors[0]} />
      <ButtonBar
        handlePrimary={handleNext}
      />
    </div>
  );
}